import { QueryClient, type QueryFunctionContext } from '@tanstack/react-query'
import axios from 'axios'
import api from '@/shared/utils/axios'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Configure how long data lives in the cache (milliseconds) without other consumers triggering additional requests.
      // Can be configured from 0 to Infinity.
      staleTime: 1000 * 60 * 60, // 1 hour
      // By default, react-query will retry failed requests up to 3 times.
      retry: false,
      retryOnMount: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
})

const MAX_RETRIES = 2

// Retries only failures a later attempt can resolve: a request that never got an answer (an OS network change
// aborts in-flight XHRs) and the gateway-class 5xx codes. Bounded per fetch, so no re-render or remount can
// restart it.
export function retryTransientFailures(failureCount: number, error: Error): boolean {
  if (failureCount >= MAX_RETRIES) return false
  if (!axios.isAxiosError(error)) return false
  if (!error.response) return true
  return [502, 503, 504].includes(error.response.status)
}

// Utility function that is intended to be used as a `queryFn` for `useQuery` or `useQueries`. It expects to
// receive the request URL from the first element of the query key array and executes the request using our
// configured axios instance.
//
// Usage:
//
// const { data } = useQuery({
//   queryKey: [pointsUrl],
//   queryFn: fetchForUrl<IPoints>,
// })
//
export async function fetchForUrl<T>({ queryKey: [url] }: QueryFunctionContext<Nullable<string>[]>): Promise<T | null> {
  if (!url) {
    return null
  }

  const result = await api.get<T>(url)
  return result.data
}
